import { clsx } from 'clsx'

/** Render an EPSS probability score with the SANS-canonical risk band.
 *
 *   ≥ 0.90  critical   "exploitation in 30 days near-certain"
 *   ≥ 0.50  high       "patch this week"
 *   ≥ 0.10  medium     "patch this month"
 *   < 0.10  low        "background patching cadence"
 *
 * EPSS is FIRST.org's data-driven probability of exploitation, complementary
 * to CVSS (severity if exploited) and KEV (already exploited). The bands are
 * the de-facto convention used across the defender community — keep them
 * consistent everywhere we surface an EPSS number.
 */

export type EpssBand = 'critical' | 'high' | 'medium' | 'low' | 'unknown'

const TONE: Record<EpssBand, string> = {
  critical: 'bg-rose/15 text-rose border-rose/30',
  high:     'bg-amber/15 text-amber-light border-amber/30',
  medium:   'bg-blue/15 text-blue-light border-blue/30',
  low:      'bg-emerald/15 text-emerald border-emerald/30',
  unknown:  'bg-surface-raised text-text-muted border-border',
}

const HELP: Record<EpssBand, string> = {
  critical: '≥0.90 — exploitation in 30 days near-certain',
  high:     '≥0.50 — patch this week',
  medium:   '0.10–0.50 — patch this month',
  low:      '<0.10 — background patching cadence',
  unknown:  'No EPSS score available',
}

export function epssBand(score: number | null | undefined): EpssBand {
  if (score == null) return 'unknown'
  if (score >= 0.90) return 'critical'
  if (score >= 0.50) return 'high'
  if (score >= 0.10) return 'medium'
  return 'low'
}

interface Props {
  score: number | null | undefined
  /** Server may send the band already computed — prefer it when present. */
  band?: EpssBand | string | null
  /** "compact" hides the score number, just shows the band label. */
  variant?: 'full' | 'compact'
  className?: string
}

export default function EpssBadge({ score, band, variant = 'full', className }: Props) {
  const b = ((band as EpssBand) || epssBand(score ?? null)) as EpssBand
  const t = TONE[b] || TONE.unknown
  const pct = score != null ? `${(score * 100).toFixed(score >= 0.10 ? 0 : 1)}%` : '—'
  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono border', t, className)}
          title={`EPSS · ${HELP[b]}`}>
      <span className="uppercase font-bold tracking-wider">{b}</span>
      {variant === 'full' && <span className="opacity-80">EPSS {pct}</span>}
    </span>
  )
}
